import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { AppError } from '@st/contract';
import { useNavigate } from 'react-router';
import { useTranslation } from '../i18n/I18nProvider';
import { toErrorMessage } from '../i18n/errorMessage';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState } from '../ui/ErrorState';
import { LockedSection } from './LockedSection';

export interface EmptyConfig {
  /** Poruka iz `@st/i18n`. */
  message: string;
  title?: string;
  actionLabel?: string;
  onAction?: () => void;
}

export interface QueryBoundaryProps<T> {
  query: Pick<UseQueryResult<T>, 'data' | 'error' | 'isPending' | 'isError' | 'refetch'>;
  /** Skeleton po obliku sadržaja — nikad spinner preko ekrana. */
  skeleton: ReactNode;
  children: (data: T) => ReactNode;
  isEmpty?: (data: T) => boolean;
  empty?: EmptyConfig;
}

/**
 * Tri stanja na jednom mjestu (ARHITEKTURA.md sekcija 10):
 * skeleton → prazno stanje s jednom akcijom → greška mapirana iz `AppError.code`.
 * Svaki ekran u portalu prolazi kroz ovu komponentu.
 */
export function QueryBoundary<T>({
  query,
  skeleton,
  children,
  isEmpty,
  empty,
}: QueryBoundaryProps<T>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (query.isPending) {
    return (
      <div aria-busy="true" aria-live="polite" data-testid="query-loading">
        <span className="sr-only">{t('a11y.loading')}</span>
        {skeleton}
      </div>
    );
  }

  /*
    `subscription_inactive` nije greška nego stanje pristupa. Otkad brava stoji na prehrani,
    mjerama, check-inu, kalendaru, navikama i porukama, klijent bez paketa bi
    na svakom od tih ekrana dobio crvenu poruku o grešci — a ništa nije
    pokvareno, samo nije plaćeno. Jedno mjesto pokriva sve te ekrane.

    `payment_required` se namjerno NE hvata ovdje: to je zaključan pojedini
    sadržaj (članak iza paketa) i ekrani koji ga imaju rješavaju ga sami, uz
    ime onoga što je zaključano.
  */
  if (query.isError && query.error instanceof AppError && query.error.code === 'subscription_inactive') {
    return <LockedSection />;
  }

  if (query.isError) {
    const msg = toErrorMessage(t, query.error);
    const secondary =
      msg.actionKey === 'login'
        ? { label: t('auth.login'), onClick: () => void navigate('/prijava') }
        : msg.actionKey === 'billing'
          ? { label: t('subscription.managePortal'), onClick: () => void navigate('/pretplata') }
          : msg.actionKey === 'plans'
            ? { label: t('empty.browsePlans'), onClick: () => void navigate('/pretplata') }
            : null;

    return (
      <ErrorState
        title={msg.title}
        help={msg.help}
        retryLabel={t('common.retry')}
        onRetry={() => void query.refetch()}
        {...(secondary ? { secondaryLabel: secondary.label, onSecondary: secondary.onClick } : {})}
      />
    );
  }

  const data = query.data as T;

  if (isEmpty?.(data) && empty) {
    return (
      <EmptyState
        message={empty.message}
        {...(empty.title ? { title: empty.title } : {})}
        {...(empty.actionLabel && empty.onAction
          ? { actionLabel: empty.actionLabel, onAction: empty.onAction }
          : {})}
      />
    );
  }

  return <>{children(data)}</>;
}
